import { useTranslation } from 'react-i18next';

import Pagination from '@klaviyo-mkt/gatsby-components/molecules/pagination';

import { getAuthorName } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';
import { useShouldDisplayBreadcrumbs } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBreadcrumbs';

import { ArchiveGrid, ArchiveWrapper, ArchiveBlogCard } from '../styles';
import * as s from './styles';

import type { IPageContext } from '../category/types';
import type { IAuthorArchiveData } from './types';

const AuthorArchiveLayout = ({
  pageContext, data: {
    wpUser: {
      description, firstName, lastName, name, url, usersAuthor,
    }, allWpPost,
  },
}: {
  data: IAuthorArchiveData;
  pageContext: IPageContext;
}) => {
  const { t, i18n } = useTranslation();
  const { shouldShowBreadcrumbs, homePrefix } = useShouldDisplayBreadcrumbs('/blog', 'default', true, i18n.language);
  const {
    numberOfPages,
    pageNumber,
    uri,
    previousPagePath,
    nextPagePath,
  } = pageContext;

  const authorName = getAuthorName(name || '', firstName, lastName);

  const breadcrumbs = [
    {
      link: homePrefix || '/',
      text: t('breadcrumbs_home'),
    },
    {
      link: `${homePrefix || '/'}blog`,
      text: t('breadcrumbs_blog'),
    },
    {
      link: uri,
      text: authorName,
    },
  ];


  return (
    <>
      { shouldShowBreadcrumbs && <s.AuthorBreadcrumbs breadcrumbs={breadcrumbs} /> }
      <ArchiveGrid>
        <ArchiveWrapper>
          <s.AuthorName variant="h1">{authorName}</s.AuthorName>
          <s.AuthorJobInfo>
            {usersAuthor?.jobTitle && (
              <s.AuthorTitle>
                {usersAuthor.jobTitle}
              </s.AuthorTitle>
            )}
            {usersAuthor?.jobTitle && usersAuthor.companyName && (
              <s.AuthorCompany>-</s.AuthorCompany>
            )}
            {url && usersAuthor?.companyName ? (
              <s.AuthorUrl to={url} rel="noopener" target="_blank">
                <s.UrlAccessibility className="screen-reader-only">
                  {`${t('templates_archive_author_openInANewTab')}`}
                </s.UrlAccessibility>

                {usersAuthor?.companyName && (
                  <s.AuthorCompany>
                    {usersAuthor.companyName}
                  </s.AuthorCompany>
                )}

                {url && (
                  <s.StyledIcon iconName="ExternalLink" />
                )}
              </s.AuthorUrl>
            ) : (
              <span>
                {usersAuthor?.companyName && (
                  <s.AuthorCompany>
                    {usersAuthor.companyName}
                  </s.AuthorCompany>
                )}
              </span>
            )}
          </s.AuthorJobInfo>
        </ArchiveWrapper>
        <s.StyledAuthor
          author={{
            description,
            firstName,
            lastName,
            name,
            uri,
            usersAuthor,
          }}
          isArchive
        />
      </ArchiveGrid>

      <ArchiveGrid>
        <ArchiveWrapper>
          <s.AuthorCardListEyebrow>
          {`${t('templates_archive_author_postsByThisAuthor')}`}
          </s.AuthorCardListEyebrow>
        </ArchiveWrapper>
        {allWpPost.nodes.map(({ title, uri: postUri, date, excerpt, featuredImage: postFeaturedImage, templatesEditorial }) => (
          <ArchiveBlogCard
            key={JSON.stringify(title + postUri + excerpt)}
            title={templatesEditorial?.postTitle?.shortTitle || title}
            categoryName={name}
            categoryLink={uri}
            uri={postUri}
            date={date}
            excerpt={excerpt}
            featuredImage={templatesEditorial?.cardImage || postFeaturedImage?.node}
          />
        ))}
        <ArchiveWrapper>
          {(numberOfPages > 1) && (
            <Pagination
              numberOfPages={numberOfPages}
              currentPage={pageNumber}
              baseUrl={uri}
              previousPagePath={previousPagePath}
              nextPagePath={nextPagePath}
            />
          )}
        </ArchiveWrapper>
      </ArchiveGrid>
    </>
  );
};

export default AuthorArchiveLayout;
